// IndexedDB 数据库工具类
export interface TankRecord {
  id: string
  type: 'water' | 'maintenance' | 'livestock'
  timestamp: number
  data: any
}

class IndexedDBService {
  private dbName = 'TankLogDB'
  private version = 1
  private db: IDBDatabase | null = null

  // 初始化数据库
  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        this.db = request.result
        resolve()
      }

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result
        
        // 创建记录存储
        if (!db.objectStoreNames.contains('records')) {
          const store = db.createObjectStore('records', { keyPath: 'id' })
          store.createIndex('type', 'type', { unique: false })
          store.createIndex('timestamp', 'timestamp', { unique: false })
        }

        // 创建照片存储
        if (!db.objectStoreNames.contains('photos')) {
          const photoStore = db.createObjectStore('photos', { keyPath: 'id' })
          photoStore.createIndex('recordId', 'recordId', { unique: false })
          photoStore.createIndex('timestamp', 'timestamp', { unique: false })
        }
      }
    })
  }

  // 检查数据库是否已初始化
  private checkDB(): void {
    if (!this.db) {
      throw new Error('IndexedDB 未初始化，请先调用 init() 方法')
    }
  }

  // 保存记录
  async saveRecord(record: TankRecord): Promise<void> {
    this.checkDB()
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['records'], 'readwrite')
      const store = transaction.objectStore('records')
      const request = store.put(record)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve()
    })
  }

  // 获取所有记录
  async getAllRecords(): Promise<TankRecord[]> {
    this.checkDB()
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['records'], 'readonly')
      const store = transaction.objectStore('records')
      const request = store.getAll()

      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result)
    })
  }

  // 根据类型获取记录
  async getRecordsByType(type: TankRecord['type']): Promise<TankRecord[]> {
    this.checkDB()
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['records'], 'readonly')
      const store = transaction.objectStore('records')
      const index = store.index('type')
      const request = index.getAll(type)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result)
    })
  }

  // 根据时间范围获取记录
  async getRecordsByTimeRange(startTime: number, endTime: number): Promise<TankRecord[]> {
    this.checkDB()
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['records'], 'readonly')
      const store = transaction.objectStore('records')
      const index = store.index('timestamp')
      const range = IDBKeyRange.bound(startTime, endTime)
      const request = index.getAll(range)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result)
    })
  }

  // 删除记录
  async deleteRecord(id: string): Promise<void> {
    this.checkDB()
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['records'], 'readwrite')
      const store = transaction.objectStore('records')
      const request = store.delete(id)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve()
    })
  }

  // 清空所有记录
  async clearAllRecords(): Promise<void> {
    this.checkDB()
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['records'], 'readwrite')
      const store = transaction.objectStore('records')
      const request = store.clear()

      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve()
    })
  }

  // 保存照片
  async savePhoto(recordId: string, photoData: string): Promise<string> {
    this.checkDB()
    
    const photoId = `photo_${Date.now()}_${Math.random().toString(36).substr(2)}`
    const photoRecord = {
      id: photoId,
      recordId,
      data: photoData,
      timestamp: Date.now()
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['photos'], 'readwrite')
      const store = transaction.objectStore('photos')
      const request = store.put(photoRecord)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(photoId)
    })
  }

  // 获取记录的照片
  async getPhotosByRecordId(recordId: string): Promise<string[]> {
    this.checkDB()
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['photos'], 'readonly')
      const store = transaction.objectStore('photos')
      const index = store.index('recordId')
      const request = index.getAll(recordId)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        const photos = request.result.map((photo: any) => photo.data)
        resolve(photos)
      }
    })
  }

  // 删除照片
  async deletePhoto(photoId: string): Promise<void> {
    this.checkDB()
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['photos'], 'readwrite')
      const store = transaction.objectStore('photos')
      const request = store.delete(photoId)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve()
    })
  }

  // 删除记录的所有照片
  async deletePhotosByRecordId(recordId: string): Promise<void> {
    this.checkDB()
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['photos'], 'readwrite')
      const store = transaction.objectStore('photos')
      const index = store.index('recordId')
      const request = index.getAllKeys(recordId)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        const photoIds = request.result
        const deletePromises = photoIds.map((photoId: IDBValidKey) => {
          return new Promise<void>((resolveDelete, rejectDelete) => {
            const deleteRequest = store.delete(photoId)
            deleteRequest.onerror = () => rejectDelete(deleteRequest.error)
            deleteRequest.onsuccess = () => resolveDelete()
          })
        })
        
        Promise.all(deletePromises)
          .then(() => resolve())
          .catch(reject)
      }
    })
  }

  // 获取数据库大小（估算）
  async getDatabaseSize(): Promise<number> {
    this.checkDB()
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['records', 'photos'], 'readonly')
      const recordsStore = transaction.objectStore('records')
      const photosStore = transaction.objectStore('photos')
      
      const recordsRequest = recordsStore.getAll()
      const photosRequest = photosStore.getAll()
      
      let totalSize = 0
      let completed = 0
      
      const checkComplete = () => {
        completed++
        if (completed === 2) {
          resolve(totalSize)
        }
      }
      
      recordsRequest.onsuccess = () => {
        const records = recordsRequest.result
        totalSize += JSON.stringify(records).length
        checkComplete()
      }
      
      photosRequest.onsuccess = () => {
        const photos = photosRequest.result
        totalSize += JSON.stringify(photos).length
        checkComplete()
      }
      
      recordsRequest.onerror = () => reject(recordsRequest.error)
      photosRequest.onerror = () => reject(photosRequest.error)
    })
  }

  // 迁移 localStorage 数据到 IndexedDB
  async migrateFromLocalStorage(): Promise<void> {
    const stored = localStorage.getItem('tankLogRecords')
    if (!stored) return

    try {
      const records = JSON.parse(stored)
      
      for (const record of records) {
        await this.saveRecord(record)
        
        // 迁移照片数据
        if (record.data.photos && Array.isArray(record.data.photos)) {
          for (const photoData of record.data.photos) {
            await this.savePhoto(record.id, photoData)
          }
          // 从记录中移除照片数据，因为现在单独存储
          delete record.data.photos
          await this.saveRecord(record)
        }
      }
      
      // 迁移完成后删除 localStorage 数据
      localStorage.removeItem('tankLogRecords')
    } catch (error) {
      console.error('数据迁移失败:', error)
    }
  }
}

// 创建单例实例
export const indexedDBService = new IndexedDBService()

// 初始化数据库并返回服务实例
export const initIndexedDB = async (): Promise<IndexedDBService> => {
  await indexedDBService.init()
  await indexedDBService.migrateFromLocalStorage()
  return indexedDBService
}